import {
  AddCircleIcon,
  FileEditIcon,
  FileIcon,
  MoreVerticalIcon,
  PlayIcon,
  StopIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { apiGet } from '@/lib/api-client';

// Types for workflow data
interface Workflow {
  id: string;
  name: string;
  description: string | null;
  status: 'active' | 'draft' | 'archived' | 'published';
  currentVersion: number;
  isTemplate: boolean;
  entityType: string;
  definition?: unknown; // JSON definition
  createdAt: string | null;
  updatedAt: string | null;
}

function WorkflowsPage() {
  const navigate = useNavigate();

  // Fetch workflows using useQuery
  const {
    data: workflows,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['workflows'],
    queryFn: async () => {
      const response = await apiGet<{ data: Workflow[]; count: number }>('/workflows');
      return response.data;
    },
  });

  const handleCreateWorkflow = () => {
    navigate({ to: '/admin/workflows/$workflowId', params: { workflowId: 'new' } });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
      case 'published':
        return (
          <Badge variant="outline" className="text-green-600 border-green-600">
            {status === 'published' ? 'Published' : 'Active'}
          </Badge>
        );
      case 'draft':
        return (
          <Badge variant="outline" className="text-yellow-600 border-yellow-600">
            Draft
          </Badge>
        );
      case 'archived':
        return (
          <Badge variant="outline" className="text-gray-600 border-gray-600">
            Archived
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Workflows</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Manage and automate business processes with visual workflows
          </p>
        </div>
        <Button onClick={handleCreateWorkflow}>
          <HugeiconsIcon icon={AddCircleIcon} className="mr-2 h-4 w-4" />
          New Workflow
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-8">
            <p className="text-muted-foreground">Error loading workflows. Please try again.</p>
          </CardContent>
        </Card>
      ) : !workflows || workflows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-8">
            <HugeiconsIcon icon={FileIcon} size={48} className="text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No workflows configured yet.</p>
            <Button variant="outline" className="mt-4" onClick={handleCreateWorkflow}>
              Create your first workflow
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {workflows.map((workflow) => (
            <Card
              key={workflow.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => {
                navigate({ to: `/admin/workflows/${workflow.id}` });
              }}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-base">{workflow.name}</CardTitle>
                    <CardDescription className="line-clamp-2 mt-1">
                      {workflow.description || 'No description'}
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <HugeiconsIcon icon={MoreVerticalIcon} className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate({ to: `/admin/workflows/${workflow.id}` });
                        }}
                      >
                        <HugeiconsIcon icon={FileEditIcon} className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          // TODO: Implement workflow run action
                          console.log('Run workflow', workflow.id);
                        }}
                      >
                        <HugeiconsIcon icon={PlayIcon} className="mr-2 h-4 w-4" />
                        Run
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          // TODO: Implement workflow stop action
                          console.log('Stop workflow', workflow.id);
                        }}
                      >
                        <HugeiconsIcon icon={StopIcon} className="mr-2 h-4 w-4" />
                        Stop
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  {getStatusBadge(workflow.status)}
                  <span className="text-xs text-muted-foreground">v{workflow.currentVersion}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/admin/workflows/')({
  component: WorkflowsPage,
});
